import { MathRenderer } from './MathRenderer';
import { LatexText } from './LatexText';
import DOMPurify from 'dompurify';

interface QuestionRendererProps {
  text: string;
  imageUrl?: string;
  mathExpression?: string;
  className?: string;
}

export function QuestionRenderer({ 
  text, 
  imageUrl, 
  mathExpression, 
  className = '' 
}: QuestionRendererProps) {
  const isHtmlContent = /<[^>]+>/.test(text);
  
  return (
    <div className={`space-y-3 ${className}`} data-testid="question-renderer">
      {isHtmlContent ? (
        <div 
          className="text-base leading-relaxed prose prose-sm max-w-none dark:prose-invert"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }}
        />
      ) : (
        <div className="text-base leading-relaxed whitespace-pre-wrap">
          <LatexText text={text} />
        </div>
      )}
      
      {mathExpression && (
        <div className="py-2 text-center">
          <MathRenderer 
            math={mathExpression} 
            displayMode={true}
            className="text-lg"
          />
        </div>
      )}
      
      {imageUrl && (
        <div className="flex justify-center py-2">
          <div className="bg-white rounded-lg border border-border p-3 w-full max-w-md overflow-visible">
            <img
              src={imageUrl}
              alt="Question illustration"
              className="w-full h-auto block"
              style={{ minHeight: '150px', aspectRatio: '4/3' }}
              data-testid="question-image"
            />
          </div>
        </div>
      )}
    </div>
  );
}

interface OptionRendererProps {
  text: string;
  imageUrl?: string;
  mathExpression?: string;
  optionLetter: string;
  className?: string;
}

export function OptionRenderer({ 
  text, 
  imageUrl, 
  mathExpression, 
  optionLetter,
  className = '' 
}: OptionRendererProps) {
  return (
    <div className={`flex items-start gap-3 ${className}`} data-testid={`option-${optionLetter.toLowerCase()}`}>
      <span className="font-semibold text-primary flex-shrink-0 mt-1">
        {optionLetter}.
      </span>
      
      <div className="flex-1 space-y-2">
        {/<[^>]+>/.test(text) ? (
          <div 
            className="text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }}
          />
        ) : (
          <div className="text-sm leading-relaxed whitespace-pre-wrap">
            <LatexText text={text} />
          </div>
        )}
        
        {mathExpression && (
          <div className="py-1">
            <MathRenderer 
              math={mathExpression} 
              displayMode={false}
              className="text-sm"
            />
          </div>
        )}
        
        {imageUrl && (
          <div className="py-1">
            <div className="bg-white rounded border border-border p-1 max-w-xs overflow-visible">
              <img
                src={imageUrl}
                alt={`Option ${optionLetter} illustration`}
                className="w-full h-auto block"
                style={{ minHeight: '60px' }}
                data-testid={`option-${optionLetter.toLowerCase()}-image`}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface ExplanationRendererProps {
  text?: string;
  imageUrl?: string;
  mathExpression?: string;
  className?: string;
}

export function ExplanationRenderer({ 
  text, 
  imageUrl, 
  mathExpression, 
  className = '' 
}: ExplanationRendererProps) {
  if (!text && !imageUrl && !mathExpression) {
    return null;
  }

  const isHtmlContent = text ? /<[^>]+>/.test(text) : false;

  return (
    <div className={`space-y-3 ${className}`} data-testid="explanation-renderer">
      {text && (
        isHtmlContent ? (
          <div 
            className="text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }}
          />
        ) : (
          <div className="text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground">
            <LatexText text={text} />
          </div>
        )
      )}
      
      {mathExpression && (
        <div className="py-2 text-center">
          <MathRenderer 
            math={mathExpression} 
            displayMode={true}
            className="text-base"
          />
        </div>
      )}
      
      {imageUrl && (
        <div className="flex justify-center py-2">
          <div className="bg-white rounded-lg border border-border p-3 w-full max-w-sm overflow-visible">
            <img
              src={imageUrl}
              alt="Explanation illustration"
              className="w-full h-auto block"
              style={{ minHeight: '100px' }}
              data-testid="explanation-image"
            />
          </div>
        </div>
      )}
    </div>
  );
}
